import React from "react";
import { Helmet } from "react-helmet";
import { Lock, EyeOff, Database, UserCheck, ShieldCheck } from "lucide-react";

export default function PrivacyOmoreGames() {
  const points = [
    {
      icon: <Database className="w-5 h-5 text-blue-500" />,
      title: "Data We Collect",
      desc: "We collect basic information like your name and email only if you send us a message via our contact form. We also track game scores (like reflex times) to help you monitor your performance."
    },
    {
      icon: <Lock className="w-5 h-5 text-blue-500" />,
      title: "How We Use Data",
      desc: "Your data is used strictly to improve your gaming experience and respond to your messages. We have a zero-spam policy and do not sell your personal information to third parties."
    },
    {
      icon: <EyeOff className="w-5 h-5 text-blue-500" />,
      title: "Cookies & Analytics",
      desc: "We use small files called 'cookies' and browser storage to remember your game settings and high scores. This ensures the site runs smoothly for you every time you return."
    },
    {
      icon: <UserCheck className="w-5 h-5 text-blue-500" />,
      title: "Your Data Rights",
      desc: "You own your data. You can ask us to delete your information or game history at any time. Simply send us a request through our secure contact portal."
    }
  ];

  return (
    <div className="min-h-screen bg-[#090a0b] text-white selection:bg-blue-500/30 font-sans">
<Helmet>

  <html lang="en" />

  <title>
    Privacy Policy | OMORE Games Data Protection, Neuralis & Browser Games Privacy
  </title>

  <meta
    name="description"
    content="Read the OMORE Games Privacy Policy. Learn how we collect, use, and protect data when you play Neuralis, reflex tests, and browser brain games on omore.games."
  />

  <meta
    name="keywords"
    content="
    omore games privacy,
    neuralis privacy,
    browser game privacy policy,
    reflex game data,
    brain game privacy,
    omore games data protection,
    neuralis data safety,
    online game privacy policy
    "
  />

  <meta name="robots" content="index, follow" />
  <meta name="googlebot" content="index, follow, max-image-preview:large" />

  <meta name="author" content="OMORE Games" />

  <link
    rel="canonical"
    href="https://omore.games/privacy"
  />


  {/* OPEN GRAPH */}

  <meta property="og:type" content="website" />

  <meta
    property="og:title"
    content="Privacy Policy | OMORE Games"
  />

  <meta
    property="og:description"
    content="Learn how OMORE Games protects your data while playing Neuralis and other brain training games."
  />

  <meta
    property="og:url"
    content="https://omore.games/privacy"
  />

  <meta
    property="og:site_name"
    content="OMORE Games"
  />

  <meta
    property="og:image"
    content="https://omore.games/og-cover.png"
  />


  {/* TWITTER */}

  <meta name="twitter:card" content="summary_large_image" />

  <meta
    name="twitter:title"
    content="Privacy Policy | OMORE Games"
  />

  <meta
    name="twitter:description"
    content="Privacy and data protection policy for OMORE Games and Neuralis."
  />

  <meta
    name="twitter:image"
    content="https://omore.games/og-cover.png"
  />


  {/* PRIVACY POLICY SCHEMA */}

  <script type="application/ld+json">
    {JSON.stringify({
      "@context": "https://schema.org",
      "@type": "PrivacyPolicy",
      name: "Privacy Policy - OMORE Games",
      url: "https://omore.games/privacy",
      publisher: {
        "@type": "Organization",
        name: "OMORE Games",
        url: "https://omore.games",
        logo: "https://omore.games/Logo.PNG"
      },
      datePublished: "2026-03-01",
      dateModified: "2026-03-12"
    })}
  </script>


  {/* ORGANIZATION SCHEMA */}

  <script type="application/ld+json">
    {JSON.stringify({
      "@context": "https://schema.org",
      "@type": "Organization",
      name: "OMORE Games",
      url: "https://omore.games",
      logo: "https://omore.games/Logo.PNG",
      founder: {
        "@type": "Person",
        name: "Omkar More"
      }
    })}
  </script>

</Helmet>

      {/* HERO SECTION */}
      <header className="relative pt-24 pb-16 md:pt-40 md:pb-24 px-6 overflow-hidden">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full h-[300px] bg-blue-600/5 blur-[120px] rounded-full" />
        
        <div className="max-w-4xl mx-auto relative z-10 text-center">
          <div className="flex justify-center mb-6">
            <ShieldCheck className="text-blue-500 w-10 h-10 opacity-60" />
          </div>
          <h1 className="text-5xl md:text-8xl font-black tracking-tighter uppercase mb-4 leading-none">
            PRIVACY <span className="text-zinc-800">POLICY</span>
          </h1>
          <p className="text-[10px] md:text-xs tracking-[0.5em] text-zinc-600 uppercase font-bold">
            Updated: March 12, 2026
          </p>
        </div>
      </header>

      {/* CONTENT SECTION */}
      <main className="pb-32 px-6">
        <article className="max-w-4xl mx-auto">
          {/* SEO Optimized Intro */}
          <section className="mb-12 text-center">
            <p className="text-zinc-400 text-sm md:text-base leading-relaxed max-w-2xl mx-auto italic font-medium">
              At OMORE GAMES, your privacy is a priority. This policy explains what information 
              we collect when you play our games (like Neuralis) or visit our site, and how we keep 
              that data secure using modern encryption and Firebase protocols.
            </p>
          </section>

          <div className="grid gap-6">
            {points.map((item, i) => (
              <section key={i} className="p-8 border border-zinc-800 bg-zinc-900/5 hover:border-blue-500/30 transition-all duration-300 rounded-sm group">
                <div className="flex items-center gap-4 mb-4">
                  <div className="group-hover:scale-110 transition-transform">
                    {item.icon}
                  </div>
                  <h2 className="text-lg font-black uppercase tracking-widest">{item.title}</h2>
                </div>
                <p className="text-zinc-200 text-sm leading-relaxed font-medium">
                  {item.desc}
                </p>
              </section>
            ))}
          </div>

          {/* SECURITY BADGE FOOTER */}
          <footer className="mt-20 pt-12 border-t border-zinc-900 text-center">
            <div className="inline-block px-6 py-2 border border-zinc-800 rounded-full mb-8">
               <p className="text-zinc-600 text-[9px] uppercase tracking-[0.3em] font-black">
                Secure System • Verified Privacy • No Third Party Selling
               </p>
            </div>
            <p className="text-zinc-200 text-[11px] font-medium tracking-wide">
              Have concerns? Email our data protection lead: <span className="text-blue-500 font-bold">hello@omkarmore.com</span>
            </p>
          </footer>
        </article>
      </main>
    </div>
  );
}